import { EFinancingProgram, EWorksheetDecision } from '@if/core/types'
import { Grid, Stack } from '@mui/material'
import { Breadcrumb, InputTextField, PageError, PageSpinner } from '@src/components'
import PendingMessage from '@src/components/PendingMessage'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import WarningMessageAccordion from '@src/containers/ViewCreditApplicationPage/components/warningMessageAccordion'
import { useCreditApplicationContext, useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetAvailableCredit } from '@src/data/api/available-credit-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { useDeleteMessage } from '@src/data/api/message/message-api'
import {
  useComputeProductsLoan,
  useUpdateProductsWorksheetDecision,
} from '@src/data/api/products-worksheet/products-worksheet-api'
import { useBusinessErrors, useProductProviders } from '@src/data/selectors/app-config-selectors'
import {
  useAreAllTasksRequiredForCVTCompleted,
  useIncomeConfirmed,
  useCurrentCreditApplicationSpecificNorms,
  useIsFinalDecisionPending,
} from '@src/data/selectors/credit-selectors'
import { useAreAllDocumentsRequiredForCVT } from '@src/data/selectors/document-selectors'
import {
  BaseWorksheetFundingThresholdExtend,
  CreditApplicationMessage,
  NormsMessage,
  WorksheetDecision,
} from '@src/data/types'
import { ProductsComputeRequestDto } from '@src/data/types/ProductsComputeRequestDto'
import { ProductsComputeResultDto } from '@src/data/types/ProductsComputeResultDto'
import { ProductsProvider } from '@src/data/types/ProductsProvider'
import {
  ProductsInsurancePlan,
  ProductsWorksheet,
  ProductsWorksheetDecisionDTO,
} from '@src/data/types/ProductsWorksheetSchema'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import translate from '@src/services/translate'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ProductsSummaryBox from '../components/LoanSummary/ProductsSummaryBox'
import WorksheetDecisionDialog from '../components/LoanSummary/WorksheetDecisionDialog'
import WorksheetDecisionSection from '../components/LoanSummary/WorksheetDecisionSection'
import TransactionSection from '../components/Transaction/TransactionSection'
import { useDialogDecisionDefaultValue, useViewTotalObligation } from '../viewWorksheet-hooks'
import { getEmptyProductsComputeResponse, getProvidersInsurancePlans } from '../viewWorksheet-selectors'

const ProductsWorksheetViewPage = () => {
  const { t } = useTranslation()

  const { creditApplication, worksheet, isLoading, privateMessages } = useCreditApplicationData()
  const productsWorksheet = worksheet as ProductsWorksheet | undefined

  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const [computeFunding] = useComputeProductsLoan()
  const [updateDecision] = useUpdateProductsWorksheetDecision()
  const [deleteMessage] = useDeleteMessage()
  const EditDecisionDialogOpen = 'EditDecisionDialogOpen'
  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')
  const [productsInsurances, setProductsInsurances] = useState<ProductsInsurancePlan[]>([])

  const [computedInfo, setComputedInfo] = useState<ProductsComputeResultDto>({} as ProductsComputeResultDto)

  const productsProviders: ProductsProvider[] = useProductProviders()
  const finalDecisionIsPending = useIsFinalDecisionPending()
  const error = useBusinessErrors()

  const { id, financingProgramId } = useCreditApplicationContext()
  const [availableCreditData] = useGetAvailableCredit(financingProgramId, id)
  const availableCredit = availableCreditData?.availableAmount ?? 0
  const creditInProgress = availableCreditData?.creditInProgress ?? 0
  const commonNormsMessage: NormsMessage[] = useCurrentCreditApplicationSpecificNorms()
  const user = useUser()

  const worksheetNormWarningMessages = useMemo(() => {
    if (!productsWorksheet?.normsDecision?.warningMessages) return []
    return productsWorksheet.normsDecision.warningMessages.map((wr) => {
      const translatedMessage = translate([`worksheet.norms.${wr.id}`])
      let messageSuffix = ' | '
      wr.parameters.forEach((p, i) => {
        if (i >= 1) messageSuffix = messageSuffix.concat(', ')
        const suffix = translate(`worksheet.paramNames.${p.name}`, { val: p.value })
        messageSuffix = messageSuffix.concat(suffix)
      })
      return {
        id: wr.id,
        message: translatedMessage.concat(messageSuffix),
        applicantType: null,
      } as NormsMessage
    })
  }, [productsWorksheet?.normsDecision?.warningMessages])

  const { documents, requiredDocuments } = useCreditApplicationData()
  const areAllTasksRequiredForCVTCompleted = useAreAllTasksRequiredForCVTCompleted()
  const areAllDocumentsRequiredForCVTApproved = useAreAllDocumentsRequiredForCVT(documents, requiredDocuments)
  const incomeConfirmed = useIncomeConfirmed()
  const totalObligation = useViewTotalObligation(productsWorksheet?.amountRequested, {
    totalInterestAmount: computedInfo?.totalInterestAmount,
  })
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')
  const activePaymentPlan = merchant?.paymentPlans?.[0] ?? null

  const editDisabled = creditApplication?.editLocked === true || finalDecisionIsPending
  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.Products}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const dialogDecisionDefaultValue = useDialogDecisionDefaultValue(
    creditApplication!,
    productsWorksheet!,
    areAllTasksRequiredForCVTCompleted,
    areAllDocumentsRequiredForCVTApproved,
    incomeConfirmed,
  )

  const handleDeletingPrivateMessage = async (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      await deleteMessage({
        id: message.id,
        creditApplicationId: message.creditApplicationId,
        financingProgramId: creditApplication.financingProgramId,
      })
    }
  }

  useEffect(() => {
    if (productsWorksheet) setProductsInsurances(getProvidersInsurancePlans(productsWorksheet, productsProviders))
  }, [productsProviders, productsWorksheet])

  useEffect(() => {
    if (creditApplication && productsWorksheet && Object.keys(computedInfo).length === 0) {
      const stateIso = creditApplication?.applicant.currentAddress.stateIso

      const computeDto: ProductsComputeRequestDto = {
        amountRequested: productsWorksheet.amountRequested ?? 0,
        frequency: productsWorksheet.paymentFrequency,
        term: productsWorksheet.term,
        stateIso: stateIso,
        firstPaymentDate: productsWorksheet.firstPaymentOn,
        deliveryOn: productsWorksheet.deliveryOn,
      }

      if (computeDto) {
        const fetchFunding = async () => {
          try {
            const data = await computeFunding(computeDto)
            setComputedInfo(data)
          } catch (error) {
            reportErrorToConsole(error)
          }
        }
        void fetchFunding()
      } else {
        const emptyComputedDto = getEmptyProductsComputeResponse()
        setComputedInfo(emptyComputedDto)
      }
    }
  }, [productsWorksheet, computedInfo, creditApplication, computeFunding])

  // callbacks
  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  const handleSaveDecision = useCallback(
    async (data: WorksheetDecision) => {
      if (productsWorksheet) {
        const updatedFinalDecision: ProductsWorksheetDecisionDTO = {
          creditApplicationId: productsWorksheet.creditApplicationId,
          decision: data.decision,
          otherReason: data.otherReason,
          versionTag: productsWorksheet.versionTag,
        } as ProductsWorksheetDecisionDTO

        if (data.decision === EWorksheetDecision.ApprovedWithCondition) {
          const fundingThresholdExtend: BaseWorksheetFundingThresholdExtend = {
            maxPmtAmount: data.maxPmtAmount,
            maxTotalAmountFinanced: data.maxTotalAmountFinanced,
            maxTermDuration: data.maxTermDuration,
          }
          updatedFinalDecision.fundingThresholdExtend = fundingThresholdExtend
        }

        try {
          await updateDecision({
            ...updatedFinalDecision,
            creditApplicationId: creditApplication?.id,
          })
          closeDialogs()
          await invalidateFullCreditApplication(creditApplication!.id, creditApplication!.financingProgramId)
        } catch (error) {
          reportErrorToConsole(error)
        }
      }
    },
    [productsWorksheet, creditApplication, closeDialogs, invalidateFullCreditApplication, updateDecision],
  )

  return (
    <div>
      <PageSpinner isLoading={isLoading} />
      {!isLoading && productsWorksheet && creditApplication && (
        <>
          <Breadcrumb trees={breadCrumbs} />
          <Stack>
            <PageError errors={error} />
            {finalDecisionIsPending && <PendingMessage />}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <InputTextField value="IF" label={t('worksheetCommon.program')} disabled />
              <InputTextField
                label={t('worksheetCommon.creditLimit')}
                disabled
                value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0}
              />
              <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
              <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />
            </Stack>
            {commonNormsMessage?.length > 0 && (
              <WarningMessageAccordion
                applicantCreditWarnings={null}
                coapplicantCreditWarnings={null}
                commonCreditWarnings={commonNormsMessage}
                worksheetWarnings={null}
                expanded={false}
              />
            )}
            <PrivateMessage
              messages={privateMessages}
              handleDeletingPrivateMessage={handleDeletingPrivateMessage}
              currentUser={user}
              creditAppCreationDate={creditApplication.createdOn}
            />
            <Grid container textAlign="center" justifyContent="space-around" p={4}>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <ProductsSummaryBox
                  worksheet={productsWorksheet}
                  computedInfo={computedInfo}
                  interestsRate={creditApplication.finalCreditDecision.interestRate}
                />
              </Grid>
              <Grid
                border="1px solid"
                borderRadius={2}
                p={4}
                size={{
                  xs: 12,
                  md: 4,
                }}
              >
                <WorksheetDecisionSection
                  worksheet={productsWorksheet}
                  onClick={() => setCurrentOpenDialog(EditDecisionDialogOpen)}
                  editDisabled={editDisabled}
                  userCanEditWorksheet={user?.rights.canEditWorksheet === true}
                />
              </Grid>
            </Grid>
            <TransactionSection
              worksheet={productsWorksheet}
              creditApplication={creditApplication}
              paymentMerchantsByIds={null}
              totalObligation={totalObligation}
              merchantPaymentPlan={activePaymentPlan ?? null}
              productsPlans={productsInsurances}
            />
          </Stack>
          <WorksheetDecisionDialog
            open={currentOpenDialog === EditDecisionDialogOpen}
            title={t('worksheet.decision')}
            onCancel={closeDialogs}
            onConfirm={handleSaveDecision}
            worksheetWarnings={worksheetNormWarningMessages}
            defaultValue={dialogDecisionDefaultValue}
            worksheet={productsWorksheet}
            creditapplication={creditApplication}
          />
        </>
      )}
    </div>
  )
}

export default ProductsWorksheetViewPage
